import { FIXED_LIMITS, Permissions, type MemberDto, type RoleDto } from '@lagerfeuer/shared';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, MoreVertical, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Avatar } from '../../components/Avatar';
import { Modal } from '../../components/Modal';
import { Menu, anchorFrom, type Anchor, type MenuItem } from '../../components/Popover';
import { SettingsHeader } from '../../components/SettingsLayout';
import { errorMessage } from '../../lib/api';
import { membersApi } from '../../lib/endpoints';
import { useAppStore } from '../../store/app';
import { displayName } from '../../store/selectors';
import { toast } from '../../store/ui';
import { canActOn, canAssignRole, hasPerm, roleColor, useActor, type Actor } from './hierarchy';

const PAGE = 100;
const dispatch = useAppStore.getState().dispatch;

const settingsMembersKey = (serverId: string, q = '') => ['settings-members', serverId, q] as const;

const TIMEOUTS = [
  { seconds: 60, label: '60 Sekunden' },
  { seconds: 300, label: '5 Minuten' },
  { seconds: 600, label: '10 Minuten' },
  { seconds: 3600, label: '1 Stunde' },
  { seconds: 86400, label: '1 Tag' },
  { seconds: 604800, label: '1 Woche' },
  { seconds: 2419200, label: '28 Tage' },
];

const DELETE_HISTORY = [
  { seconds: 0, label: 'Nichts löschen' },
  { seconds: 3600, label: 'Letzte Stunde' },
  { seconds: 21600, label: 'Letzte 6 Stunden' },
  { seconds: 86400, label: 'Letzte 24 Stunden' },
  { seconds: 259200, label: 'Letzte 3 Tage' },
  { seconds: 604800, label: 'Letzte 7 Tage' },
];

type Action = { type: 'nickname' | 'timeout' | 'kick' | 'ban'; member: MemberDto } | null;

function isTimedOut(m: MemberDto): boolean {
  return !!m.timeoutUntil && new Date(m.timeoutUntil).getTime() > Date.now();
}

/** Tab „Mitglieder": Suche, Rollen zuweisen, Spitzname, Timeout, Kick, Bann (jeweils mit Hierarchie). */
export function MembersTab({ serverId }: { serverId: string }) {
  const actor = useActor(serverId);
  const roles = useAppStore((s) => s.roles[serverId]);
  const live = useAppStore((s) => s.members[serverId]);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<Action>(null);
  const [menu, setMenu] = useState<{ anchor: Anchor; items: MenuItem[]; testId: string } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const q = useInfiniteQuery({
    queryKey: settingsMembersKey(serverId, debounced),
    queryFn: ({ pageParam }) => membersApi.list(serverId, { q: debounced || undefined, after: pageParam, limit: PAGE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const members = useMemo(() => {
    const items = q.data?.pages.flatMap((p) => p.items) ?? [];
    return items.filter((m) => !removed.has(m.user.id)).map((m) => live?.[m.user.id] ?? m);
  }, [q.data, live, removed]);

  if (!actor || !roles) return null;
  const sortedRoles = roles.filter((r) => !r.isEveryone).sort((a, b) => b.position - a.position);

  const setRoles = async (m: MemberDto, role: RoleDto, add: boolean) => {
    try {
      if (add) await membersApi.addRole(serverId, m.user.id, role.id);
      else await membersApi.removeRole(serverId, m.user.id, role.id);
      const roleIds = add ? [...new Set([...m.roleIds, role.id])] : m.roleIds.filter((id) => id !== role.id);
      dispatch('SERVER_MEMBER_UPDATE', { ...m, roleIds });
    } catch (e) {
      toast.error('Rollen konnten nicht geändert werden', errorMessage(e));
    }
  };

  const roleMenu = (m: MemberDto, anchor: Anchor) => {
    const assignable = sortedRoles.filter((r) => canAssignRole(actor, r) && !m.roleIds.includes(r.id));
    setMenu({
      anchor,
      testId: 'member-role-menu',
      items:
        assignable.length > 0
          ? assignable.map((r) => ({
              id: r.id,
              label: (
                <span className="row" style={{ gap: '0.4rem' }}>
                  <span className="role-dot" style={{ background: roleColor(r.color) ?? 'var(--lf-text-faint)' }} aria-hidden="true" />
                  {r.name}
                </span>
              ),
              onSelect: () => void setRoles(m, r, true),
            }))
          : [{ id: 'none', label: 'Keine weiteren Rollen vergebbar', disabled: true }],
    });
  };

  const actionsMenu = (m: MemberDto, anchor: Anchor) => {
    const self = m.user.id === actor.meId;
    const allowed = canActOn(actor, roles, m);
    const timedOut = isTimedOut(m);
    setMenu({
      anchor,
      testId: 'member-actions-menu',
      items: [
        {
          id: 'nickname',
          label: 'Spitznamen ändern',
          hidden: self ? !hasPerm(actor, Permissions.CHANGE_NICKNAME) : !hasPerm(actor, Permissions.MANAGE_NICKNAMES),
          disabled: !self && !allowed,
          onSelect: () => setAction({ type: 'nickname', member: m }),
        },
        {
          id: 'timeout',
          label: timedOut ? 'Timeout aufheben' : 'Timeout',
          hidden: self || !hasPerm(actor, Permissions.MODERATE_MEMBERS),
          disabled: !allowed,
          onSelect: () =>
            timedOut
              ? void membersApi
                  .update(serverId, m.user.id, { timeoutUntil: null })
                  .then((res) => dispatch('SERVER_MEMBER_UPDATE', res))
                  .catch((e) => toast.error('Fehler', errorMessage(e)))
              : setAction({ type: 'timeout', member: m }),
        },
        { id: 'kick', label: 'Entfernen (Kick)', danger: true, separatorBefore: true, hidden: self || !hasPerm(actor, Permissions.KICK_MEMBERS), disabled: !allowed, onSelect: () => setAction({ type: 'kick', member: m }) },
        { id: 'ban', label: 'Bannen', danger: true, hidden: self || !hasPerm(actor, Permissions.BAN_MEMBERS), disabled: !allowed, onSelect: () => setAction({ type: 'ban', member: m }) },
      ],
    });
  };

  const gone = (m: MemberDto) => {
    // Das Gateway-Ereignis SERVER_MEMBER_REMOVE aktualisiert den Store; hier nur aus der Liste nehmen.
    setRemoved((s) => new Set(s).add(m.user.id));
  };

  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Mitglieder">Rollen vergeben, Spitznamen ändern und moderieren. Aktionen gegen Mitglieder mit gleich hoher oder höherer Rolle sind gesperrt.</SettingsHeader>
      <div className="search-input">
        <Search size={16} aria-hidden="true" />
        <input className="input" placeholder="Mitglieder suchen" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Mitglieder suchen" data-testid="member-search" />
      </div>
      {q.isPending ? (
        <div className="empty">
          <span className="spinner" aria-label="Lädt" />
        </div>
      ) : q.isError ? (
        <p className="error-text" role="alert">
          {errorMessage(q.error)}
        </p>
      ) : members.length === 0 ? (
        <p className="empty" data-testid="member-empty">
          Keine Mitglieder gefunden.
        </p>
      ) : (
        <ul className="settings-list" data-testid="member-list">
          {members.map((m) => (
            <MemberRow
              key={m.user.id}
              member={m}
              roles={sortedRoles}
              actor={actor}
              allRoles={roles}
              onAddRole={(a) => roleMenu(m, a)}
              onRemoveRole={(r) => void setRoles(m, r, false)}
              onActions={(a) => actionsMenu(m, a)}
            />
          ))}
        </ul>
      )}
      {q.hasNextPage && (
        <button className="btn btn-ghost" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage} data-testid="member-load-more">
          {q.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
        </button>
      )}
      {menu && <Menu anchor={menu.anchor} items={menu.items} onClose={() => setMenu(null)} testId={menu.testId} />}
      {action?.type === 'nickname' && <NicknameDialog serverId={serverId} member={action.member} self={action.member.user.id === actor.meId} onClose={() => setAction(null)} />}
      {action?.type === 'timeout' && <TimeoutDialog serverId={serverId} member={action.member} onClose={() => setAction(null)} />}
      {(action?.type === 'kick' || action?.type === 'ban') && (
        <KickBanDialog serverId={serverId} member={action.member} ban={action.type === 'ban'} onDone={() => gone(action.member)} onClose={() => setAction(null)} />
      )}
    </div>
  );
}

function MemberRow({
  member: m,
  roles,
  allRoles,
  actor,
  onAddRole,
  onRemoveRole,
  onActions,
}: {
  member: MemberDto;
  roles: RoleDto[];
  allRoles: RoleDto[];
  actor: Actor;
  onAddRole: (a: Anchor) => void;
  onRemoveRole: (r: RoleDto) => void;
  onActions: (a: Anchor) => void;
}) {
  const own = roles.filter((r) => m.roleIds.includes(r.id));
  const canManageRoles = hasPerm(actor, Permissions.MANAGE_ROLES) && (canActOn(actor, allRoles, m) || m.user.id === actor.meId || actor.isOwner);
  const timedOut = isTimedOut(m);
  return (
    <li className="settings-row member-settings-row" data-testid="member-row" data-user-id={m.user.id}>
      <Avatar user={m.user} overrideUrl={m.serverAvatarUrl} size={36} />
      <div className="member-settings-main">
        <div className="row" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
          <strong className="truncate">{displayName(m.user, m)}</strong>
          <span className="muted small">@{m.user.username}</span>
          {m.user.id === actor.ownerId && <span className="tag">Besitzer</span>}
          {timedOut && (
            <span className="tag tag-warn" title={`Timeout bis ${new Date(m.timeoutUntil!).toLocaleString('de-DE')}`} data-testid="member-timed-out">
              <Clock size={12} aria-hidden="true" /> Timeout
            </span>
          )}
        </div>
        <ul className="role-chips" aria-label="Rollen">
          {own.map((r) => (
            <li key={r.id} className="role-chip" data-testid="member-role-chip">
              <span className="role-dot" style={{ background: roleColor(r.color) ?? 'var(--lf-text-faint)' }} aria-hidden="true" />
              {r.name}
              {canManageRoles && canAssignRole(actor, r) && (
                <button className="role-chip-x" aria-label={`Rolle ${r.name} entfernen`} onClick={() => onRemoveRole(r)} data-testid="member-role-remove">
                  <X size={12} />
                </button>
              )}
            </li>
          ))}
          {canManageRoles && (
            <li>
              <button className="role-chip role-chip-add" aria-label="Rolle hinzufügen" onClick={(e) => onAddRole(anchorFrom(e.currentTarget))} data-testid="member-role-add">
                <Plus size={12} />
              </button>
            </li>
          )}
        </ul>
      </div>
      <button className="icon-btn" aria-label={`Aktionen für ${displayName(m.user, m)}`} aria-haspopup="menu" onClick={(e) => onActions(anchorFrom(e.currentTarget))} data-testid="member-actions">
        <MoreVertical size={18} />
      </button>
    </li>
  );
}

function NicknameDialog({ serverId, member, self, onClose }: { serverId: string; member: MemberDto; self: boolean; onClose: () => void }) {
  const [nick, setNick] = useState(member.nickname ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const nickname = nick.trim() || null;
      const res = self ? await membersApi.updateMe(serverId, { nickname }) : await membersApi.update(serverId, member.user.id, { nickname });
      dispatch('SERVER_MEMBER_UPDATE', res);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Spitznamen ändern"
      onClose={onClose}
      testId="nickname-dialog"
      footer={
        <>
          <button className="btn btn-ghost" onClick={() => setNick('')} type="button">
            Zurücksetzen
          </button>
          <button className="btn btn-primary" type="submit" form="nickname-form" disabled={busy} data-testid="nickname-save">
            Speichern
          </button>
        </>
      }
    >
      <form id="nickname-form" className="field" onSubmit={submit}>
        <label htmlFor="nickname-input">Spitzname für {member.user.username}</label>
        <input id="nickname-input" className="input" value={nick} maxLength={FIXED_LIMITS.nicknameMax} placeholder={member.user.displayName ?? member.user.username} onChange={(e) => setNick(e.target.value)} data-testid="nickname-input" />
        {error && (
          <span className="error-text" role="alert">
            {error}
          </span>
        )}
      </form>
    </Modal>
  );
}

function TimeoutDialog({ serverId, member, onClose }: { serverId: string; member: MemberDto; onClose: () => void }) {
  const [seconds, setSeconds] = useState(3600);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    try {
      const until = new Date(Date.now() + seconds * 1000).toISOString();
      dispatch('SERVER_MEMBER_UPDATE', await membersApi.update(serverId, member.user.id, { timeoutUntil: until }, reason.trim() || undefined));
      toast.success(`${displayName(member.user, member)} ist im Timeout`);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Timeout für ${displayName(member.user, member)}`}
      onClose={onClose}
      testId="timeout-dialog"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn btn-danger" onClick={() => void submit()} disabled={busy} data-testid="timeout-submit">
            Timeout vergeben
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted" style={{ margin: 0 }}>
          Im Timeout kann das Mitglied nur lesen – nicht schreiben, reagieren oder sprechen.
        </p>
        <fieldset className="radio-group">
          <legend className="label">Dauer</legend>
          {TIMEOUTS.map((t) => (
            <label key={t.seconds} className="radio-card compact">
              <input type="radio" name="timeout" checked={seconds === t.seconds} onChange={() => setSeconds(t.seconds)} data-testid={`timeout-${t.seconds}`} />
              {t.label}
            </label>
          ))}
        </fieldset>
        <div className="field">
          <label htmlFor="timeout-reason">Grund (optional, Audit-Log)</label>
          <input id="timeout-reason" className="input" value={reason} maxLength={512} onChange={(e) => setReason(e.target.value)} data-testid="timeout-reason" />
        </div>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function KickBanDialog({ serverId, member, ban, onDone, onClose }: { serverId: string; member: MemberDto; ban: boolean; onDone: () => void; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [deleteSeconds, setDeleteSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = displayName(member.user, member);
  const submit = async () => {
    setBusy(true);
    try {
      const r = reason.trim() || undefined;
      if (ban) await membersApi.ban(serverId, member.user.id, { reason: r, deleteMessageSeconds: deleteSeconds });
      else await membersApi.kick(serverId, member.user.id, r);
      onDone();
      void qc.invalidateQueries({ queryKey: ['bans', serverId] });
      toast.success(ban ? `${name} wurde gebannt` : `${name} wurde entfernt`);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={ban ? `${name} bannen` : `${name} entfernen`}
      onClose={onClose}
      testId={ban ? 'ban-dialog' : 'kick-dialog'}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn btn-danger" onClick={() => void submit()} disabled={busy} data-testid={ban ? 'ban-submit' : 'kick-submit'}>
            {ban ? 'Bannen' : 'Entfernen'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted" style={{ margin: 0 }}>
          {ban ? 'Ein Bann entfernt das Mitglied und verhindert einen erneuten Beitritt, bis er aufgehoben wird.' : 'Das Mitglied kann mit einer neuen Einladung wieder beitreten.'}
        </p>
        {ban && (
          <div className="field">
            <label htmlFor="ban-delete">Nachrichtenverlauf löschen</label>
            <select id="ban-delete" className="select" value={deleteSeconds} onChange={(e) => setDeleteSeconds(Number(e.target.value))} data-testid="ban-delete-messages">
              {DELETE_HISTORY.map((d) => (
                <option key={d.seconds} value={d.seconds}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label htmlFor="kickban-reason">Grund (optional)</label>
          <textarea id="kickban-reason" className="textarea" rows={2} maxLength={512} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={ban ? 'ban-reason' : 'kick-reason'} />
        </div>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
